import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../config/environment';
import {
  validateMobileNumber,
  formatMobileNumber,
  sendOTP,
  verifyOTP,
  clearStoredOTP,
  formatCountdown,
} from '../utils/otpUtils';
import './AccountInformationSection.css';

const OTP_RESEND_SECONDS = 60;

const AccountInformationSection = ({ className = '' }) => {
  const { user, updateProfile } = useAuth();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [primaryPhone, setPrimaryPhone] = useState('');
  const [phoneNumbers, setPhoneNumbers] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });

  const [editEmail, setEditEmail] = useState(false);
  const [changePhoneOpen, setChangePhoneOpen] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [phoneVerificationToken, setPhoneVerificationToken] = useState(null);
  const [otpBusy, setOtpBusy] = useState(false);

  const showMessage = (type, text) => {
    setSaveMessage({ type, text });
    if (type === 'success') {
      setTimeout(() => setSaveMessage({ type: '', text: '' }), 4000);
    }
  };

  const loadUserProfile = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      if (!token) {
        showMessage('error', 'Authentication required. Please login again.');
        return;
      }

      const response = await fetch(getApiUrl('/users/profile/me'), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();

      if (data.success) {
        setFullName(data.data.name || '');
        setEmail(data.data.email || '');
        setPrimaryPhone(data.data.phone || '');
        setPhoneNumbers(data.data.phoneNumbers?.filter((p) => p.type !== 'primary') || []);
      } else {
        showMessage('error', data.message || 'Failed to load profile');
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      showMessage('error', 'Failed to load profile data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadUserProfile();
    }
  }, [user, loadUserProfile]);

  useEffect(() => {
    if (otpTimer <= 0) return undefined;
    const id = setInterval(() => {
      setOtpTimer((t) => (t > 0 ? t - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [otpTimer]);

  const resetPhoneChangeFlow = () => {
    setChangePhoneOpen(false);
    setNewPhone('');
    setOtpCode('');
    setOtpSent(false);
    setOtpVerified(false);
    setOtpTimer(0);
    setPhoneVerificationToken(null);
    clearStoredOTP();
  };

  const handleNewPhoneChange = (e) => {
    setNewPhone(formatMobileNumber(e.target.value));
    setOtpSent(false);
    setOtpVerified(false);
    setOtpCode('');
    setPhoneVerificationToken(null);
    clearStoredOTP();
  };

  const handleSendOtp = async () => {
    if (!validateMobileNumber(newPhone)) {
      showMessage('error', 'Enter a valid mobile number (03XXXXXXXXX).');
      return;
    }
    if (primaryPhone && newPhone.trim() === primaryPhone.trim()) {
      showMessage('error', 'This is already your current number.');
      return;
    }

    setOtpBusy(true);
    setSaveMessage({ type: '', text: '' });
    const result = await sendOTP(newPhone.trim());
    setOtpBusy(false);

    if (result.success) {
      setOtpSent(true);
      setOtpTimer(OTP_RESEND_SECONDS);
      showMessage('success', result.message || 'Verification code sent to your phone.');
    } else {
      showMessage('error', result.message || 'Failed to send verification code.');
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode.trim()) {
      showMessage('error', 'Enter the 6-digit code.');
      return;
    }

    setOtpBusy(true);
    const result = await verifyOTP(otpCode.trim(), newPhone.trim());
    setOtpBusy(false);

    if (result.success) {
      setOtpVerified(true);
      setPhoneVerificationToken(result.phoneVerificationToken || null);
      showMessage('success', result.message || 'Mobile number verified.');
    } else {
      showMessage('error', result.message || 'Invalid or expired code.');
    }
  };

  const confirmPhoneChange = async () => {
    if (!otpVerified) {
      showMessage('error', 'Verify your new number with the WhatsApp code first.');
      return;
    }
    if (!phoneVerificationToken) {
      showMessage(
        'error',
        'Verification token missing. Verify the code again or contact support.'
      );
      return;
    }

    try {
      setIsLoading(true);
      const result = await updateProfile({
        phone: newPhone.trim(),
        phoneVerificationToken,
      });
      if (result.success) {
        setPrimaryPhone(newPhone.trim());
        resetPhoneChangeFlow();
        showMessage('success', 'Mobile number updated successfully.');
        await loadUserProfile();
      }
    } catch (error) {
      showMessage('error', error.message || 'Failed to update mobile number.');
    } finally {
      setIsLoading(false);
    }
  };

  const saveAccountChanges = async () => {
    try {
      setIsLoading(true);
      setSaveMessage({ type: '', text: '' });

      const payload = {
        name: fullName.trim(),
        phoneNumbers: [
          { number: primaryPhone, type: 'primary', isVerified: true },
          ...phoneNumbers.filter((p) => p.number?.trim()),
        ],
      };

      if (editEmail && email.trim()) {
        payload.email = email.trim();
      }

      const result = await updateProfile(payload);
      if (result.success) {
        if (editEmail) setEditEmail(false);
        showMessage('success', result.message || 'Profile updated successfully.');
        await loadUserProfile();
      }
    } catch (error) {
      showMessage('error', error.message || 'Failed to save changes');
    } finally {
      setIsLoading(false);
    }
  };

  const addPhoneNumber = () => {
    setPhoneNumbers([
      ...phoneNumbers,
      { number: '', type: 'secondary', isVerified: false },
    ]);
  };

  const removePhoneNumber = (index) => {
    setPhoneNumbers(phoneNumbers.filter((_, i) => i !== index));
  };

  const updatePhoneNumber = (index, field, value) => {
    const next = [...phoneNumbers];
    next[index] = { ...next[index], [field]: value };
    setPhoneNumbers(next);
  };

  return (
    <div className={`account-information-section ${className}`.trim()}>
      {isLoading && !changePhoneOpen && (
        <div className="account-info-loading">
          <div className="loading-spinner" />
          <p>Loading profile data...</p>
        </div>
      )}

      {saveMessage.text && (
        <div className={`account-info-message ${saveMessage.type}`}>{saveMessage.text}</div>
      )}

      <div className="account-info-field">
        <div className="account-info-field-header">
          <label htmlFor="account-full-name">Full Name</label>
          <span className="field-optional-hint">Optional to update</span>
        </div>
        <input
          id="account-full-name"
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder="Enter your full name"
          className="account-info-input"
          autoComplete="name"
        />
      </div>

      <div className="account-info-field">
        <div className="account-info-field-header">
          <label htmlFor="account-email">Email Address</label>
          {!editEmail ? (
            <button
              type="button"
              className="account-info-link-btn"
              onClick={() => setEditEmail(true)}
            >
              Change email
            </button>
          ) : (
            <button
              type="button"
              className="account-info-link-btn muted"
              onClick={() => {
                setEditEmail(false);
                loadUserProfile();
              }}
            >
              Cancel
            </button>
          )}
        </div>
        <input
          id="account-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={!editEmail}
          className={`account-info-input${!editEmail ? ' readonly' : ''}`}
          autoComplete="email"
        />
        <small className="account-info-hint">
          {editEmail
            ? 'Save changes below to update your email.'
            : 'Tap “Change email” if you need to update it.'}
        </small>
      </div>

      <div className="account-info-field">
        <div className="account-info-field-header">
          <label htmlFor="account-primary-phone">Primary Mobile</label>
          {!changePhoneOpen && (
            <button
              type="button"
              className="account-info-link-btn"
              onClick={() => {
                setChangePhoneOpen(true);
                setSaveMessage({ type: '', text: '' });
              }}
            >
              Change number
            </button>
          )}
        </div>
        <input
          id="account-primary-phone"
          type="tel"
          value={primaryPhone}
          disabled
          className="account-info-input readonly"
          autoComplete="tel"
        />

        {changePhoneOpen && (
          <div className="phone-change-panel">
            <p className="phone-change-intro">
              Enter your new mobile number. We will send a one-time WhatsApp code to verify it.
            </p>
            <label htmlFor="account-new-phone">New mobile number</label>
            <input
              id="account-new-phone"
              type="tel"
              value={newPhone}
              onChange={handleNewPhoneChange}
              placeholder="03XXXXXXXXX"
              className="account-info-input"
              autoComplete="tel"
            />

            <div className="phone-change-actions">
              {!otpSent ? (
                <button
                  type="button"
                  className="account-info-btn secondary"
                  onClick={handleSendOtp}
                  disabled={otpBusy || isLoading}
                >
                  {otpBusy ? 'Sending…' : 'Send WhatsApp code'}
                </button>
              ) : (
                <>
                  <label htmlFor="account-otp-code">Verification code</label>
                  <input
                    id="account-otp-code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="6-digit code"
                    className="account-info-input otp-input"
                    disabled={otpVerified}
                  />
                  {!otpVerified && (
                    <button
                      type="button"
                      className="account-info-btn secondary"
                      onClick={handleVerifyOtp}
                      disabled={otpBusy || isLoading}
                    >
                      {otpBusy ? 'Verifying…' : 'Verify code'}
                    </button>
                  )}
                  {otpTimer > 0 ? (
                    <span className="otp-resend-hint">Resend in {formatCountdown(otpTimer)}</span>
                  ) : (
                    <button
                      type="button"
                      className="account-info-link-btn"
                      onClick={handleSendOtp}
                      disabled={otpBusy}
                    >
                      Resend code
                    </button>
                  )}
                </>
              )}
            </div>

            {otpVerified && (
              <button
                type="button"
                className="account-info-btn primary"
                onClick={confirmPhoneChange}
                disabled={isLoading}
              >
                {isLoading ? 'Updating…' : 'Confirm new number'}
              </button>
            )}

            <button
              type="button"
              className="account-info-link-btn cancel-phone-change"
              onClick={resetPhoneChangeFlow}
            >
              Cancel phone change
            </button>
          </div>
        )}
      </div>

      <div className="account-info-field">
        <label>Additional phone numbers</label>
        {phoneNumbers.map((phone, index) => (
          <div key={index} className="extra-phone-row">
            <input
              type="tel"
              value={phone.number}
              onChange={(e) => updatePhoneNumber(index, 'number', e.target.value)}
              placeholder="03XXXXXXXXX"
              className="account-info-input"
            />
            <select
              value={phone.type}
              onChange={(e) => updatePhoneNumber(index, 'type', e.target.value)}
              className="extra-phone-type"
            >
              <option value="secondary">Secondary</option>
              <option value="work">Work</option>
              <option value="emergency">Emergency</option>
            </select>
            <button
              type="button"
              className="account-info-btn danger-outline"
              onClick={() => removePhoneNumber(index)}
            >
              Remove
            </button>
          </div>
        ))}
        <button type="button" className="account-info-btn ghost" onClick={addPhoneNumber}>
          + Add phone number
        </button>
      </div>

      <button
        type="button"
        className="account-info-save-btn"
        onClick={saveAccountChanges}
        disabled={isLoading}
      >
        {isLoading ? 'Saving…' : 'Save changes'}
      </button>
    </div>
  );
};

export default AccountInformationSection;
